<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    ref="contentBody"
    data-type="content-body"
    class="prose lg:prose-lg prose-figure:-mx-8 prose-figure:mb-12 sm:prose-img:rounded-xl lg:prose-figure:-mx-24 xl:prose-figure:-mx-32 [&_iframe]:w-full [&_iframe]:h-auto [&_iframe]:aspect-video sm:[&_iframe]:shadow-xl sm:[&_iframe]:rounded-xl prose-figcaption:text-sm prose-figcaption:text-center [&_h1]:scroll-mt-[132px] [&_h2]:scroll-mt-[132px] [&_h3]:scroll-mt-[132px] [&_h4]:scroll-mt-[132px] [&_h5]:scroll-mt-[132px] [&_h6]:scroll-mt-[132px]"
    v-html="sanitizedHTML"
  />
</template>

<script setup lang="ts">
import usePopularRvsElement from './usePopularRvsElement'

const props = defineProps<{
  htmlBody: string
}>()

const { $captureError } = useNuxtApp()
const { isPotentiallyABot } = useBotScore()

/**
 * Registers the Ghost `<popular-rvs>` custom element here rather than in a
 * parent, so every Ghost body gets it, posts and pages alike. The IIFE that
 * would otherwise upgrade the tag is stripped from the HTML below.
 */
usePopularRvsElement()

const sanitizedHTML = computed(() => {
  const html = stripPopularRvsScripts(props.htmlBody)

  if (loaded.value) {
    return openLinksInNewWindow({
      content: html,
      currentPath: window.location.pathname,
    })
  }

  // Replace anything in between <figure class="kg-card kg-embed-card"> and
  // </figure>, as it seems bots are chocking on these embeds if there's too
  // many of them.
  if (isPotentiallyABot) {
    return html.replace(
      /<figure class="kg-card kg-embed-card">[\s\S]*?<\/figure>/g,
      '',
    )
  }

  return html
})

const loaded = ref(false)
const contentBody = useTemplateRef('contentBody')

// 100px header height + 32px offset
const topOffset = 132

function runHashScroll() {
  nextTick(() => initializePosition())
}

const nuxtApp = useNuxtApp()
onMounted(() => {
  try {
    runHashScroll()
    nuxtApp.hooks.hookOnce('page:loading:end', runHashScroll)
  }
  catch (err) {
    $captureError(err)
  }
})

function scrollToHashHeading(): boolean {
  const hash = window.location.hash
  if (!hash || hash.startsWith('#:~:')) {
    return true
  }

  const id = normalizeAttributeId(hash.slice(1))
  const heading = document.getElementById(id)
    ?? contentBody.value?.querySelector(`#${CSS.escape(id)}`)

  if (!heading) {
    return false
  }

  const top = window.scrollY + heading.getBoundingClientRect().top - topOffset
  window.scrollTo({
    top: Math.max(0, top),
    behavior: 'smooth',
  })

  return true
}

function initializePosition(attemptsLeft = 10) {
  // Repositions viewport on load / on refresh if there is a hash in the URL.
  // Section titles are hidden by the header nav bar; this avoids that.
  if (scrollToHashHeading() || attemptsLeft <= 0) {
    return
  }

  requestAnimationFrame(() => initializePosition(attemptsLeft - 1))
}
</script>
